import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import GallerySection from '../components/GallerySection';
import { getOptimizedImageUrl } from '../lib/cms';
import SEO from '../components/SEO';

const BOOKING_URL = 'https://cryobybreum.planway.com/';

export default function About() {
  return (
    <div className="animate-in fade-in duration-200">
      <SEO preset="about" />
      {/* 1. HERO */}
      <section className="py-16 sm:py-20 bg-sand-light/60 border-b border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-xs text-taupe mb-6" aria-label="Brødkrummesti">
            <Link to="/" className="hover:text-espresso transition-colors">
              Forside
            </Link>
            <span className="text-taupe-border">/</span>
            <span className="text-espresso font-medium">Om Cryo by Breum</span>
          </nav>

          <h1 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-semibold text-espresso uppercase tracking-wider">
            Om Cryo by Breum
          </h1>

          <p className="text-base sm:text-lg text-taupe mt-4 leading-relaxed">
            En personlig og eksklusiv klinik for kropsbehandlinger og målrettet træning i rolige, feminine rammer på Dalumvej 82 i Odense SV.
          </p>
        </div>
      </section>

      {/* 2. CLINIC STORY & ATMOSPHERE */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center reveal-on-scroll">
            {/* Story Text */}
            <div className="lg:col-span-7 space-y-6">
              <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
                Et trygt frirum med fokus på dit velbefindende
              </h2>
              <div className="w-16 h-px bg-accent-gold my-4"></div>
              
              <div className="text-taupe text-base sm:text-lg leading-relaxed space-y-4">
                <p>
                  <Link to="/" className="text-espresso font-semibold hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/50 hover:decoration-accent-brown transition-colors">
                    Cryo by Breum
                  </Link>{' '}
                  blev etableret med en klar vision om at tilbyde moderne kropsbehandlinger og skånsom træning i et roligt, varmt og professionelt miljø. 
                </p>
                <p>
                  I en travl hverdag er det afgørende at have et sted, hvor der er tid og ro til at prioritere kroppen. Hos Cryo by Breum lægger vi vægt på den personlige dialog og en afslappet atmosfære, hvor du altid er i centrum.
                </p>
                <p>
                  Uanset om du vælger{' '}
                  <Link to="/behandlinger/fedtfrysning" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    kontrolleret fedtfrysning
                  </Link>
                  ,{' '}
                  <Link to="/behandlinger/ems-traening" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    tidsoptimeret EMS-muskelstimulation
                  </Link>{' '}
                  eller{' '}
                  <Link to="/behandlinger/body-roll" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                    lymfedrænerende Body Roll
                  </Link>
                  , foregår det i lyse, indbydende behandlingsrum indrettet med fokus på velvære og diskretion.
                </p>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent-brown text-white text-xs uppercase tracking-widest font-semibold hover:bg-accent-brown-hover transition-colors shadow-xs"
                >
                  <span>Book tid</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                </a>

                <Link
                  to="/priser"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-sand hover:bg-sand-dark text-espresso text-xs uppercase tracking-widest font-semibold border border-taupe-border/80 transition-colors"
                >
                  <span>Se vores priser</span>
                </Link>

                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-sand hover:bg-sand-dark text-espresso text-xs uppercase tracking-widest font-semibold border border-taupe-border/80 transition-colors"
                >
                  <span>Kontakt klinikken</span>
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="lg:col-span-5">
              <div className="overflow-hidden shadow-xl border border-taupe-border bg-sand aspect-[4/5]">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-reception-og-ventevaerelse.jpg', { width: 800, quality: 90 })}
                  alt="Lyst og indbydende receptionsområde og venteværelse med sildebensparket hos Cryo by Breum i Odense"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. CLINIC VALUES */}
      <section className="py-24 bg-white border-y border-taupe-border/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal-on-scroll">
            <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
              Hvad du kan forvente hos os
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-taupe-border/80 reveal-on-scroll delay-150">
            <div className="py-8 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0 space-y-4">
              <h3 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider">
                Rolige & æstetiske rammer
              </h3>
              <p className="text-sm text-taupe leading-relaxed">
                Vi har skabt en varm og harmonisk klinik, hvor du kan koble fra og nyde din behandling i uforstyrrede omgivelser.
              </p>
            </div>

            <div className="py-8 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0 space-y-4">
              <h3 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider">
                Tryghed & dialog
              </h3>
              <p className="text-sm text-taupe leading-relaxed">
                Vi lægger stor vægt på en grundig indledende snak, så du føler dig fuldstændig tryg og informeret om din behandling.
              </p>
            </div>

            <div className="py-8 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0 space-y-4">
              <h3 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider">
                Moderne teknologi
              </h3>
              <p className="text-sm text-taupe leading-relaxed">
                Vores udstyr er udvalgt med henblik på kontrolleret og skånsom behandling, hvad enten det gælder kulde, EMS eller infrarød varme.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. GALLERY */}
      <GallerySection
        gallerySlug="klinikken"
        title="Billeder fra klinikken"
        subtitle="Se vores moderne behandlingsrum og faciliteter på Dalumvej 82 i Odense SV"
      />

      {/* 5. LOCATION & INFO */}
      <section className="py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso uppercase tracking-wider">
            Dalumvej 82, 5250 Odense SV
          </h2>
          <p className="text-taupe text-base leading-relaxed max-w-xl mx-auto">
            Klinikken er let tilgængelig i Dalum med nem parkering. Tidsbestilling foregår nemt og bekvemt via vores online booking.
          </p>
          <div className="pt-2">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-9 py-4 bg-accent-brown text-white text-xs font-semibold uppercase tracking-widest hover:bg-accent-brown-hover transition-colors shadow-xs"
            >
              <span>Book tid hos Cryo by Breum</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
